import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { EntitySelect, EntitySelectEntity, Input } from '@factorialco/f0-react/dist/experimental'
import { AvatarNameSelectorDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { BasicTaskHandlerSchema } from 'modules/procurement/components/procurementTypes/Workflows/sidePanels/schemas/basicTaskHandler'
import { HandlerFormComponent } from 'modules/workflows/contexts/WorkflowContext'
import { getDurationUnitOptions } from 'modules/workflows/lib/durationUnit'

export const BasicTaskHandlerForm = ({}: HandlerFormComponent) => {
  const { control, watch } = useFormContext<BasicTaskHandlerSchema>()

  const { company } = useCurrentSession()

  const { loading: employeesLoading, data: employeesData } = useQuery(AvatarNameSelectorDocument, {
    variables: {
      companyId: company.id,
      active: true,
    },
  })

  const selectedAssigneeIds = watch('nodeData.payload.assignee_ids') ?? []

  const availableEmployees = employeesData?.employees?.employeeNames || []
  const selectedAssigneeEntities = availableEmployees
    .filter((emp) => selectedAssigneeIds.includes(emp.id))
    .map((emp) => ({
      id: emp.id,
      name: emp.fullName,
      avatar: buildUrl(emp.avatar?.url),
    }))

  return (
    <Box gap="s16">
      {/* Task Title */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.title_label')}
        </Text>
        <Controller
          name="nodeData.payload.title"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              size="md"
              label={i18n.t('workflows.sidepanel.task.title_label')}
              hideLabel
              placeholder={i18n.t('workflows.sidepanel.task.title_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Task Description */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.description_label')}
        </Text>
        <Controller
          name="nodeData.payload.description"
          control={control}
          render={({ field, fieldState }) => (
            <Input
              size="md"
              label={i18n.t('workflows.sidepanel.task.description_label')}
              hideLabel
              placeholder={i18n.t('workflows.sidepanel.task.description_placeholder')}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Assignees */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.assignees_label')}
        </Text>
        <Controller
          name="nodeData.payload.assignee_ids"
          control={control}
          render={({ field, fieldState }) => (
            <EntitySelect
              label={i18n.t('workflows.sidepanel.task.assignees_select_label')}
              hideLabel
              loading={employeesLoading}
              entities={availableEmployees.map((emp) => ({
                id: emp.id,
                name: emp.fullName,
                avatar: buildUrl(emp.avatar?.url),
                expanded: false,
              }))}
              notFoundSubtitle={i18n.t('workflows.sidepanel.task.assignees_not_found_subtitle')}
              notFoundTitle={i18n.t('workflows.sidepanel.task.assignees_not_found_title')}
              onGroupChange={() => {}}
              onItemExpandedChange={() => {}}
              size="md"
              onSelect={(selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
                if (Array.isArray(selection)) {
                  field.onChange(selection.map((s) => s.id))
                } else if (selection) {
                  field.onChange([selection.id])
                } else {
                  field.onChange([])
                }
              }}
              searchPlaceholder={i18n.t('workflows.sidepanel.task.assignees_search_placeholder')}
              selectedEntities={selectedAssigneeEntities}
              selectedLabel={i18n.t('workflows.sidepanel.task.assignees_selected_label')}
              singleSelector={false}
              placeholder={i18n.t('workflows.sidepanel.task.assignees_placeholder')}
              selectedItemsCopy={i18n.t('workflows.sidepanel.task.assignees_selected_copy')}
              groups={[]}
              selectedGroup="all"
              clearLabel={i18n.t('workflows.sidepanel.task.assignees_clear_label')}
              selectAllLabel={i18n.t('workflows.sidepanel.task.assignees_select_all_label')}
              error={fieldState.error?.message}
            />
          )}
        />
      </Box>

      {/* Due On */}
      <Box gap="s8" width="full">
        <Text size="200" weight="medium" className="text-f1-foreground-secondary">
          {i18n.t('workflows.sidepanel.task.due_on_label')}
        </Text>
        <Box flexDirection="row" gap="s8" width="full" alignItems="baseline">
          <Box style={{ width: '100px' }}>
            <Controller
              name="nodeData.payload.due_on_value"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  size="md"
                  label={i18n.t('workflows.sidepanel.task.due_on_label')}
                  hideLabel
                  placeholder="2"
                  value={String(field.value)}
                  onChange={(value) => field.onChange(Number(value))}
                  error={fieldState.error?.message}
                  type="number"
                />
              )}
            />
          </Box>
          <Box width="full">
            <Controller
              name="nodeData.payload.due_on_unit"
              control={control}
              render={({ field, fieldState }) => (
                <F0Select
                  size="md"
                  label={i18n.t('workflows.sidepanel.task.due_on_label')}
                  hideLabel
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  options={getDurationUnitOptions()}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
